// SCR-CAT-07 Suppliers: list, sheet and card (frozen mockups-cat-v1). Owner: everything; Storekeeper: view, no
// amounts (GEN-15, ADR-077). Purchase figures arrive with P4.
import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  Filters,
  FormGrid,
  Input,
  List,
  ListRow,
  Notice,
  SearchField,
  SheetActions,
  SkeletonList,
  Tabs,
} from '../../design/kit';
import { deleteSupplier, saveSupplier, toggleSupplier, type Supplier } from '../../data/catalog';
import { PURCHASING, loadOrders, loadReceipts, loadReturns, loadSupplierProducts } from '../../data/purchasing';
import { OrderRows, ReceiptRows, ReturnRows } from '../purchasing/PurchasingPage';
import type { Language } from '../../i18n';
import { formatDate, formatMoney } from '../../lib/format';
import { Code, Name } from '../../i18n/Isolate';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import {
  CATALOG,
  initials,
  parseWhole,
  refusalCode,
  useMoney,
  useRefusalText,
  useSuppliers,
  useTab,
} from '../catalog/common';

type Status = 'active' | 'inactive' | 'all';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SuppliersPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const owner = role === 'owner';
  const navigate = useNavigate();
  const money = useMoney();
  const suppliers = useSuppliers();
  const [text, setText] = useState('');
  const [status, setStatus] = useState<Status>('active');
  const [creating, setCreating] = useState(false);
  const list = useMemo(() => {
    const q = text.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    return (suppliers.data ?? []).filter(
      (s) =>
        (status === 'all' || s.active === (status === 'active')) &&
        (!q ||
          s.name.toLowerCase().includes(q) ||
          (digits.length > 0 && (s.phone ?? '').replace(/\D/g, '').includes(digits))),
    );
  }, [suppliers.data, text, status]);

  return (
    <>
      {owner && (
        <div className="yv-chips">
          <Button pill variant="main" icon="plus" onClick={() => setCreating(true)}>
            {t('newsupplier')}
          </Button>
        </div>
      )}
      <Filters>
        <SearchField value={text} onChange={setText} label={t('search.partners')} />
        <Chips
          label={t('status')}
          items={[
            { id: 'active', label: t('f.active') },
            { id: 'inactive', label: t('f.inactive') },
            { id: 'all', label: t('f.all') },
          ]}
          selected={[status]}
          onToggle={(id) => setStatus(id as Status)}
        />
      </Filters>
      {!suppliers.data ? (
        <SkeletonList label={t('loading')} />
      ) : list.length === 0 ? (
        <EmptyState icon="truck" title={t('nomatch')} text={t('nomatch.text')} />
      ) : (
        <List label={t('p.suppliers')}>
          {list.map((s) => (
            <ListRow
              key={s.id}
              testId={`supplier-${s.name}`}
              lead={{ initials: initials(s.name) }}
              title={<Name>{s.name}</Name>}
              onOpen={() => navigate(`/suppliers/${s.id}`)}
              sub={
                <>
                  {s.phone && (
                    <>
                      <Code>{s.phone}</Code> ·{' '}
                    </>
                  )}
                  {t('bought.n', { n: s.products_bought })}
                </>
              }
              value={owner && s.purchases_this_year !== null ? money(s.purchases_this_year) : undefined}
              badge={
                <>
                  {s.open_orders > 0 && <Badge tone="info">{`${t('openorders')} ${s.open_orders}`}</Badge>}
                  {s.late_orders > 0 && <Badge tone="danger">{t('late')}</Badge>}
                  {!s.active && <Badge>{t('inactive')}</Badge>}
                </>
              }
            />
          ))}
        </List>
      )}
      {creating && (
        <SupplierSheet
          supplier={null}
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            setCreating(false);
            navigate(`/suppliers/${id}`);
          }}
        />
      )}
    </>
  );
}

type CardTab = 'contact' | 'bought' | 'purchases';
const CARD_TABS: CardTab[] = ['contact', 'bought', 'purchases'];

export function SupplierCard() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { role } = useSession();
  const owner = role === 'owner';
  const navigate = useNavigate();
  const suppliers = useSuppliers();
  const supplier = suppliers.data?.find((s) => s.id === id);
  const [tab, setTab] = useTab(CARD_TABS, 'contact');
  const [editing, setEditing] = useState(false);
  const back = useCallback(() => navigate('/suppliers'), [navigate]);
  useCardHeader(supplier ? <Name>{supplier.name}</Name> : t('p.suppliers'), back);

  if (suppliers.isLoading) return <SkeletonList label={t('loading')} />;
  if (!supplier) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  return (
    <>
      <Tabs
        label={t('c7.title')}
        value={tab}
        onChange={(v) => setTab(v as CardTab)}
        items={CARD_TABS.map((v) => ({ id: v, label: t(`tab.${v}`) }))}
      />
      {tab === 'contact' && (
        <>
          <List label={t('tab.contact')}>
            <ListRow
              lead={{ icon: 'phone' }}
              title={t('phone')}
              value={supplier.phone ? <Code>{supplier.phone}</Code> : '—'}
            />
            <ListRow
              lead={{ icon: 'info' }}
              title={t('email')}
              sub={supplier.email ? <Code>{supplier.email}</Code> : '—'}
            />
            <ListRow
              lead={{ icon: 'truck' }}
              title={t('address')}
              sub={supplier.address ? <Name>{supplier.address}</Name> : '—'}
            />
            <ListRow
              lead={{ icon: 'file' }}
              title={t('taxref')}
              value={supplier.tax_ref ? <Code>{supplier.tax_ref}</Code> : '—'}
            />
            <ListRow
              lead={{ icon: 'clock' }}
              title={t('payterm')}
              value={supplier.payment_term_days !== null ? t('days', { n: supplier.payment_term_days }) : '—'}
            />
            {supplier.notes && (
              <ListRow lead={{ icon: 'file' }} title={t('notes')} sub={<Name>{supplier.notes}</Name>} />
            )}
            <ListRow
              lead={{ icon: 'check' }}
              title={t('status')}
              badge={supplier.active ? <Badge tone="success">{t('active')}</Badge> : <Badge>{t('inactive')}</Badge>}
            />
          </List>
          {owner && (
            <div className="yv-chips">
              <Button pill onClick={() => setEditing(true)}>
                {t('edit.btn')}
              </Button>
            </div>
          )}
        </>
      )}
      {tab === 'bought' && <SupplierBought supplier={supplier.id} />}
      {tab === 'purchases' && <SupplierPurchases supplier={supplier.id} />}
      {editing && (
        <SupplierSheet
          supplier={supplier}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setEditing(false);
            if (!saved) navigate('/suppliers');
          }}
        />
      )}
    </>
  );
}

/** SCR-PUR-07: products bought from this supplier; the owner also sees the last price (GEN-15). */
function SupplierBought({ supplier }: { supplier: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role } = useSession();
  const bought = useQuery({
    queryKey: [...PURCHASING, 'bought', supplier],
    queryFn: () => loadSupplierProducts(supplier),
  });
  if (!bought.data) return <SkeletonList label={t('loading')} />;
  if (bought.data.length === 0) return <EmptyState icon="tag" title={t('tab.bought')} text={t('bought.empty')} />;
  return (
    <List label={t('tab.bought')}>
      {bought.data.map((b) => (
        <ListRow
          key={b.product_id}
          lead={{ icon: 'tag' }}
          title={<Name>{b.product}</Name>}
          sub={
            <>
              {b.sku && <Code>{b.sku}</Code>}
              {b.last_date && ` · ${t('bought.last', { d: formatDate(b.last_date, lang) })}`}
            </>
          }
          value={
            role === 'owner' && b.last_price !== null
              ? `${formatMoney(b.last_price, lang)} ${t(b.last_unit === 'box' ? 'perbox' : 'perpiece')}`
              : undefined
          }
        />
      ))}
    </List>
  );
}

const PURCHASE_FILTERS = ['pf.orders', 'pf.receipts', 'pf.returns'] as const;
/** SCR-PUR-07: this supplier's orders, receipts and returns, one list filtered by chips. */
function SupplierPurchases({ supplier }: { supplier: string }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<(typeof PURCHASE_FILTERS)[number]>('pf.orders');
  const orders = useQuery({
    queryKey: [...PURCHASING, 'orders', supplier],
    queryFn: () => loadOrders(supplier),
    enabled: filter === 'pf.orders',
  });
  const receipts = useQuery({
    queryKey: [...PURCHASING, 'receipts', supplier],
    queryFn: () => loadReceipts(supplier),
    enabled: filter === 'pf.receipts',
  });
  const returns = useQuery({
    queryKey: [...PURCHASING, 'returns', supplier],
    queryFn: () => loadReturns(supplier),
    enabled: filter === 'pf.returns',
  });
  const list = filter === 'pf.orders' ? orders : filter === 'pf.receipts' ? receipts : returns;
  return (
    <>
      <Chips
        label={t('tab.purchases')}
        items={PURCHASE_FILTERS.map((k) => ({ id: k, label: t(k) }))}
        selected={[filter]}
        onToggle={(k) => setFilter(k as (typeof PURCHASE_FILTERS)[number])}
      />
      {!list.data ? (
        <SkeletonList label={t('loading')} />
      ) : list.data.length === 0 ? (
        <EmptyState icon="cart" title={t('purch.empty')} text={t('purch.empty.text')} />
      ) : filter === 'pf.orders' ? (
        <OrderRows orders={orders.data ?? []} />
      ) : filter === 'pf.receipts' ? (
        <ReceiptRows receipts={receipts.data ?? []} />
      ) : (
        <ReturnRows returns={returns.data ?? []} />
      )}
    </>
  );
}

type Errors = Partial<Record<'name' | 'email' | 'term' | 'form', string>>;

function SupplierSheet({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: Supplier | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState(supplier?.name ?? '');
  const [phone, setPhone] = useState(supplier?.phone ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [address, setAddress] = useState(supplier?.address ?? '');
  const [taxRef, setTaxRef] = useState(supplier?.tax_ref ?? '');
  const [term, setTerm] = useState(supplier?.payment_term_days?.toString() ?? '');
  const [notes, setNotes] = useState(supplier?.notes ?? '');
  const [errors, setErrors] = useState<Errors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = () => void qc.invalidateQueries({ queryKey: CATALOG });
  const save = useMutation({
    mutationFn: () =>
      saveSupplier({
        id: supplier?.id ?? null,
        name: name.trim(),
        phone,
        email: email.trim(),
        address,
        taxRef,
        paymentTermDays: parseWhole(term),
        notes,
      }),
    onSuccess: (r) => {
      done();
      onSaved(r.id);
    },
    onError: (e) => {
      const code = refusalCode(e);
      const text = refusal(e, 'err.supname');
      const field: keyof Errors =
        code === 'NAME_TAKEN' || code === 'NAME_REQUIRED'
          ? 'name'
          : code === 'INVALID_EMAIL'
            ? 'email'
            : code === 'INVALID_VALUE'
              ? 'term'
              : 'form';
      setErrors({
        [field]: field === 'email' ? t('err.email.sup') : text,
        ...(field !== 'form' ? { form: t('err.formhead') } : {}),
      });
    },
  });
  const toggle = useMutation({
    mutationFn: () => toggleSupplier(supplier?.id ?? '', !supplier?.active),
    onSuccess: () => {
      done();
      onClose();
    },
    onError: (e) => setErrors({ form: refusal(e) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteSupplier(supplier?.id ?? ''),
    onSuccess: () => {
      done();
      onSaved('');
    },
    onError: (e) => setErrors({ form: refusal(e) }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!name.trim()) next.name = t('err.required');
    if (email.trim() && !EMAIL.test(email.trim())) next.email = t('err.email.sup');
    const days = parseWhole(term);
    if (days !== null && (Number.isNaN(days) || days < 0 || days > 365)) next.term = t('err.number');
    if (Object.keys(next).length) return setErrors({ ...next, form: t('err.formhead') });
    setErrors({});
    save.mutate();
  };
  return (
    <BottomSheet
      title={t(supplier ? 'editsupplier' : 'newsupplier')}
      open
      onClose={onClose}
      busy={save.isPending || toggle.isPending || remove.isPending}
    >
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {errors.form && <Notice tone="danger">{errors.form}</Notice>}
        <Field label={t('name')} error={errors.name}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <FormGrid>
          <Field label={t('phone')}>
            {({ id }) => (
              <Input id={id} dir="ltr" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            )}
          </Field>
          <Field label={t('email')} error={errors.email}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                dir="ltr"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </Field>
        </FormGrid>
        <Field label={t('address')}>
          {({ id }) => <Input id={id} dir="auto" value={address} onChange={(e) => setAddress(e.target.value)} />}
        </Field>
        <FormGrid>
          <Field label={t('taxref')}>
            {({ id }) => <Input id={id} dir="ltr" value={taxRef} onChange={(e) => setTaxRef(e.target.value)} />}
          </Field>
          <Field label={t('payterm')} hint={t('payterm.hint')} error={errors.term}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                numeric
                inputMode="numeric"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </Field>
        </FormGrid>
        <Field label={t('notes')}>
          {({ id }) => <Input id={id} dir="auto" value={notes} onChange={(e) => setNotes(e.target.value)} />}
        </Field>
        {supplier && (
          <>
            <Button
              variant={supplier.active ? 'danger' : 'default'}
              block
              busy={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {supplier.active ? t('deactivate') : t('activate')}
            </Button>
            {!supplier.has_history &&
              (confirmDelete ? (
                <>
                  <Notice tone="warning">{t('delete.confirm')}</Notice>
                  <Button variant="danger" block busy={remove.isPending} onClick={() => remove.mutate()}>
                    {t('delete')}
                  </Button>
                </>
              ) : (
                <Button variant="quiet" block onClick={() => setConfirmDelete(true)}>
                  {t('delete')}
                </Button>
              ))}
          </>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}
